import * as React from 'react';
import styled from '@emotion/styled';
import * as Colors from '../../../consts/variables';
import { config } from '../../../config';

import { InfoTransparentIcon } from '../../../components/Icons/InfoTransparentIcon';
import { PackageLabelIcon } from '../../../components/Icons/PackageLabelIcon';
import { Link } from '../../../components/Link';
import { Modal } from '../../../components/Modal';
import { TrackComponent } from '../../../components/TrackComponent';
import { eventTracker } from '../../../helpers/EventTracker/EventTracker';
import { __ } from '../../../helpers/TranslationService';
import { normalizeLabels } from '../../../helpers/labels';
import { ISearchResultProps } from '../SearchPage';
import { SearchResultItemBottomSection } from '../components/SearchResultItemBottomSection';
import { SearchResultItemMiddleSection } from '../components/SearchResultItemMiddleSection';
import { SearchResultItemTopSection } from '../components/SearchResultItemTopSection';
import { SearchResultItemMiddleDesktopSection } from './SearchResultItemMiddleDesktopSection';
import { SearchResultItemOverlay } from './SearchResultItemOverlay';
import { SearchResultItemTopDesktopSection } from './SearchResultItemTopDesktopSection';
import {
    C1AU_DOMAIN,
    C1UK_DOMAIN,
    DE_DOMAIN,
    NL_DOMAIN,
    CRUISE_AWAY_DOMAIN,
    MarketContext,
} from '../../../context/markets';
import { css } from '@emotion/react';
import { BreakpointsEnum } from '../../../helpers/breakpoints';
import { getPackageDataByMarket } from '../helper/packageData';
interface IState {
    showModal: boolean;
}

const eventTrackingData = (props: ISearchResultProps, action: 'click' | 'impression') => ({
    button_type: action,
    company_name: props.cruise.companyTitle,
    cruise_id: props.cruise.cruiseNid,
    price: props.cruise.cheapestSail.price,
    end_date: new Date(props.cruise.cheapestSail.arrival * 1000).toLocaleDateString('de-DE'),
    start_date: new Date(props.cruise.cheapestSail.departure * 1000).toLocaleDateString('de-DE'),
    cruise_title: props.cruise.routeTitle,
});

const eventPackageTrackingData = {
    category: 'search results',
    event: 'click',
    action: 'search result click',
    label: 'dreamlines package',
};

let style: any = {
    wrapper: {
        cursor: 'pointer',
        backgroundColor: Colors.lightGray,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        margin: '25px 0',
    },
    topSection: {},
    middleSection: {
        backgroundColor: Colors.white,
        display: 'flex',
        marginTop: '-5px',
        flexDirection: 'column',
        padding: '0px 8px',
    },
    bottomSection: {},

    bestSeller: {
        width: '220px',
        color: Colors.white,
        backgroundColor: '#ff9900',
        padding: '0px 10px',
    },
    specialResultDiv: {
        display: 'flex',
        fontSize: '15px',
        textAlign: 'center',
        height: '24px',
        lineHeight: '24px',
        width: '220px',
        fontWeight: '700',
        zIndex: '1',
        alignSelf: 'center',
        justifyContent: 'center',
        marginTop: '-12px',
    },
    arrowLeft: {
        width: 0,
        height: 0,
        borderTop: '12px solid transparent',
        borderBottom: '12px solid transparent',
        borderRight: '8px solid',
    },
    arrowRight: {
        width: 0,
        height: 0,
        borderTop: '12px solid transparent',
        borderBottom: '12px solid transparent',
        borderLeft: '8px solid',
    },
};

const C1Background = (theme) =>
    css`
        background: url(${config.images}/common/c1_package_desktop_bg.svg) right center no-repeat,
            linear-gradient(
                to right,
                ${theme.colors.darkCornFlower[100]},
                ${theme.colors.imperialBlue[100]}
            );
        @media (max-width: ${BreakpointsEnum.sm}) {
            background: url(${config.images}/common/c1_package_desktop_bg.svg) right -100px center,
                linear-gradient(
                    to right,
                    ${theme.colors.darkCornFlower[100]},
                    ${theme.colors.imperialBlue[100]}
                );
        }
    `;
const DefaultBackground = css`
    background-image: url(${config.images}/common/dl_package_desktop_bg.svg);
`;

const PackageLabelWrapper = styled.div<{ isCruise1st: boolean }>`
    background-repeat: no-repeat;
    background-color: ${({ theme }) => theme.background.brandColor1};
    background-position: right center;
    min-height: 49px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 15px 0 -25px 0;
    ${({ isCruise1st, theme }) => (isCruise1st ? C1Background(theme) : DefaultBackground)}
`;

const PackageLabelText = styled.div`
    display: flex;
    align-items: center;
    font-size: 20px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.white[100]};
    span {
        font-weight: 700;
        margin-right: 5px;
    }
`;

const PackageLabelButton = styled.div`
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.white[100]};
    cursor: pointer;
    span {
        display: none;
    }
    @media (min-width: ${BreakpointsEnum.sm}) {
        span {
            display: inline-block;
        }
    }
`;

const PackageLabelIconStyled = styled(PackageLabelIcon)`
    margin: -1px 10px;
`;

const InfoTransparentIconStyled = styled(InfoTransparentIcon)`
    margin: -1px 15px 0 10px;
`;

export class SearchResultItem extends React.Component<ISearchResultProps, IState> {
    static contextType = MarketContext;
    constructor(props) {
        super(props);
        this.state = {
            showModal: false,
        };
    }

    handleModalClick = () => {
        this.setState((currentState) => ({ showModal: !currentState.showModal }));
    };

    shouldComponentUpdate(nextProps, nextState) {
        // We don't want to update the component when the search result is coming in the
        // visible area but we need to send it to eventrecorder.

        if (nextProps.isVisible && this.props.isVisible !== nextProps.isVisible) {
            eventTracker.trackG4Event('search_results', eventTrackingData(nextProps, 'impression'));
        }
        return (
            this.state.showModal !== nextState.showModal ||
            this.props.cruise !== nextProps.cruise ||
            this.props.isFavorite !== nextProps.isFavorite ||
            this.props.hidePackageFrame !== nextProps.hidePackageFrame ||
            this.props.discountThreshold !== nextProps.discountThreshold ||
            this.props.searchPageType !== nextProps.searchPageType
        );
    }

    render() {
        const { cruise, deviceType, sortString, portalId } = this.props;
        const { isPackageCruise, ugcLabels, discountSail, cheapestSail } = cruise;
        const { isCruise1st } = this.context.markets;

        const iconProps = { width: 18, height: 18 };

        const modalStyle = {
            maxWidth: '550px',
        };

        const selectedSail = sortString?.includes('discount') ? discountSail : cheapestSail;

        const isBestSeller =
            ugcLabels?.find((ugcLabel) => ugcLabel.label === 'bestseller') === undefined
                ? false
                : true;

        const { cruisePageUrl } = cruise;
        const cruisePageUrlWithoutHash = cruisePageUrl.slice(0, cruisePageUrl.lastIndexOf('#'));
        const searchResultItemUrl =
            cruisePageUrlWithoutHash + '#selectedSail=' + selectedSail.sailNid;
        const TrackableLink = TrackComponent(
            Link,
            isPackageCruise
                ? eventPackageTrackingData
                : { event: 'click', category: 'search result', action: 'click' },
        );

        const bottomSectionProps = {
            ugcLabels: normalizeLabels({
                ugcLabels: ugcLabels || [],
                excludeCluster: ['productData'],
            }),
        };

        const packageData = getPackageDataByMarket(portalId, cruise.companyTitle);

        return (
            <>
                {isPackageCruise && (
                    <PackageLabelWrapper isCruise1st={isCruise1st} className="packageLabelWrapper">
                        <PackageLabelText>
                            <PackageLabelIconStyled {...iconProps} />
                            {packageData.title}
                        </PackageLabelText>
                        <PackageLabelButton onClick={this.handleModalClick}>
                            <span>{__('read more', 'dreamlines')}</span>
                            <InfoTransparentIconStyled {...iconProps} />
                        </PackageLabelButton>
                    </PackageLabelWrapper>
                )}

                <TrackableLink
                    style={style.wrapper}
                    href={searchResultItemUrl}
                    forceReload={deviceType !== 'desktop'}
                    target={deviceType === 'desktop' ? '_self' : '_blank'}
                    className="searchResultItemContainer"
                >
                    {isBestSeller && !isPackageCruise && (
                        <div
                            style={{ ...style.specialResultDiv, color: '#ff9900' }}
                            className="searchResultItemLabel"
                        >
                            <div
                                style={style.arrowLeft}
                                className="searchResultItemLabelLeftArrow"
                            />
                            <div style={style.bestSeller} className="searchResultItemLabelText">
                                {' '}
                                {__('bestseller', 'dreamlines').toUpperCase()}{' '}
                            </div>
                            <div
                                style={style.arrowRight}
                                className="searchResultItemLabelRightArrow"
                            />
                        </div>
                    )}

                    {deviceType !== 'desktop' ? (
                        <>
                            <div style={style.topSection}>
                                <SearchResultItemTopSection {...this.props} />
                            </div>
                            <div style={style.middleSection}>
                                <SearchResultItemMiddleSection {...this.props} />
                            </div>
                            {this.props.searchPageType !== 'mapWithNoUGC' && (
                                <div style={style.bottomSection}>
                                    <SearchResultItemBottomSection {...bottomSectionProps} />
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <SearchResultItemTopDesktopSection {...this.props} />
                            <SearchResultItemMiddleDesktopSection
                                {...{ ...this.props, selectedSail }}
                            />
                        </>
                    )}
                </TrackableLink>

                {this.state.showModal && (
                    <Modal style={modalStyle} isOpen onClose={this.handleModalClick}>
                        <SearchResultItemOverlay
                            {...{ ...this.props, selectedSail }}
                            packagesUrl={packageData.url}
                        />
                    </Modal>
                )}
            </>
        );
    }
}
